import { useAppStore } from "@/stores/useStore";
import { Card as LensCard, Subject } from "@/types";
import { useRouter } from "expo-router";
import { Archive, ChevronLeft, Plus, Save } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import { Alert, Pressable, ScrollView, StyleSheet, View } from "react-native";
import {
  Button,
  Card,
  Divider,
  Text,
  TextInput,
} from "react-native-paper";

const createContentId = (prefix: string, value: string) => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);

  return `${prefix}-${slug || Date.now()}`;
};

const toTags = (value: string) =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const toTagText = (tags?: string[]) => (tags ?? []).join(", ");

const createBlankCard = (subjectId: string): LensCard => ({
  id: "",
  subject_id: subjectId,
  title: "",
  lens_name: "",
  definition: "",
  tool_name: "",
  explanation: "",
  effect: "",
  example: "",
  application_example: "",
  illustration_url: "",
  thinking_tags: [],
  career_tags: [],
  related_card_ids: [],
  is_active: true,
});

export default function LibraryCmsScreen() {
  const router = useRouter();
  const {
    archiveCard,
    cards,
    currentUser,
    loadLibraryContent,
    subjects,
    upsertCard,
    upsertSubject,
  } = useAppStore();
  const canManage = false;
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [cardForm, setCardForm] = useState<LensCard | null>(null);
  const [thinkingTags, setThinkingTags] = useState("");
  const [careerTags, setCareerTags] = useState("");
  const [newSubjectName, setNewSubjectName] = useState("");
  const [newSubjectColor, setNewSubjectColor] = useState("#3182ce");
  const [isSaving, setIsSaving] = useState(false);

  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === cardForm?.subject_id),
    [cardForm?.subject_id, subjects],
  );

  useEffect(() => {
    loadLibraryContent().catch((error) => {
      console.warn("Failed to load library content", error);
    });
  }, [loadLibraryContent]);

  useEffect(() => {
    const selectedCard = cards.find((card) => card.id === selectedCardId);
    if (!selectedCard) return;

    setCardForm(selectedCard);
    setThinkingTags(toTagText(selectedCard.thinking_tags));
    setCareerTags(toTagText(selectedCard.career_tags));
  }, [cards, selectedCardId]);

  const startNewCard = () => {
    const subjectId = subjects[0]?.id ?? "sub-1";
    setSelectedCardId(null);
    setCardForm(createBlankCard(subjectId));
    setThinkingTags("");
    setCareerTags("");
  };

  const updateCardForm = (updates: Partial<LensCard>) => {
    setCardForm((current) => (current ? { ...current, ...updates } : current));
  };

  const handleSaveCard = async () => {
    if (!cardForm) return;

    const title = cardForm.title.trim();
    if (!title || !cardForm.subject_id || !cardForm.definition.trim()) {
      Alert.alert("入力が必要です", "カード名、教科、定義を入力してください。");
      return;
    }

    setIsSaving(true);
    try {
      const cardToSave: LensCard = {
        ...cardForm,
        id: cardForm.id || createContentId("card", title),
        title,
        lens_name: cardForm.lens_name.trim() || `${title}を見るレンズ`,
        definition: cardForm.definition.trim(),
        tool_name: cardForm.tool_name.trim() || title,
        explanation: cardForm.explanation.trim() || cardForm.definition.trim(),
        effect: cardForm.effect.trim() || "日常の発見を整理しやすくする。",
        example: cardForm.example.trim() || "身近な出来事に当てはめて考える。",
        application_example: cardForm.application_example?.trim(),
        illustration_url: cardForm.illustration_url?.trim(),
        thinking_tags: toTags(thinkingTags),
        career_tags: toTags(careerTags),
        is_active: true,
      };

      await upsertCard(cardToSave);
      setSelectedCardId(cardToSave.id);
      setCardForm(cardToSave);
      Alert.alert("保存しました", "学問レンズ図書館に反映されました。");
    } catch (error) {
      console.warn("Failed to save card", error);
      Alert.alert("保存できませんでした", "権限と通信状態を確認してください。");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveCard = async () => {
    if (!cardForm?.id) return;

    Alert.alert("カードを非公開にしますか？", "投稿履歴は残したまま、図書館と投稿選択から外します。", [
      { text: "キャンセル", style: "cancel" },
      {
        text: "非公開",
        style: "destructive",
        onPress: () => {
          archiveCard(cardForm.id)
            .then(() => {
              setSelectedCardId(null);
              setCardForm(null);
            })
            .catch((error) => {
              console.warn("Failed to archive card", error);
              Alert.alert("非公開にできませんでした", "もう一度お試しください。");
            });
        },
      },
    ]);
  };

  const handleAddSubject = async () => {
    const name = newSubjectName.trim();
    if (!name) return;

    const subject: Subject = {
      id: createContentId("sub", name),
      name,
      color_code: newSubjectColor.trim() || "#3182ce",
      is_active: true,
      sort_order: subjects.length + 1,
    };

    try {
      await upsertSubject(subject);
      setNewSubjectName("");
      setNewSubjectColor("#3182ce");
    } catch (error) {
      console.warn("Failed to add subject", error);
      Alert.alert("教科を追加できませんでした", "権限と通信状態を確認してください。");
    }
  };

  if (!canManage) {
    return (
      <View style={styles.centered}>
        <Text variant="titleMedium" style={styles.centeredTitle}>
          図書館管理は別サイトへ移行します
        </Text>
        <Text style={styles.centeredText}>
          学問レンズ本文の追加・編集は、セキュリティ強化のため管理サイトで行います。
          このアプリでは図書館の閲覧と利用に限定します。
        </Text>
        <Button mode="contained" onPress={() => router.back()}>
          戻る
        </Button>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ChevronLeft size={24} color="#1f2937" />
        </Pressable>
        <View style={styles.headerText}>
          <Text variant="headlineSmall" style={styles.title}>
            学問レンズ図書館CMS
          </Text>
          <Text style={styles.subtitle}>
            カードを追加・修正して、投稿画面と図書館へ反映します。
          </Text>
        </View>
      </View>

      <Card style={styles.panel}>
        <Card.Content>
          <View style={styles.actionRow}>
            <Button
              mode="contained"
              icon={() => <Plus size={16} color="#fff" />}
              onPress={startNewCard}
              buttonColor="#3182ce"
            >
              新規カード
            </Button>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.panel}>
        <Card.Content>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            教科を追加
          </Text>
          <View style={styles.subjectForm}>
            <TextInput
              mode="outlined"
              label="教科名"
              value={newSubjectName}
              onChangeText={setNewSubjectName}
              style={styles.subjectNameInput}
            />
            <TextInput
              mode="outlined"
              label="色"
              value={newSubjectColor}
              onChangeText={setNewSubjectColor}
              style={styles.colorInput}
            />
            <Button mode="outlined" onPress={handleAddSubject}>
              追加
            </Button>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.panel}>
        <Card.Content>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            編集するカード
          </Text>
          <View style={styles.cardList}>
            {cards.map((card) => {
              const subject = subjects.find((item) => item.id === card.subject_id);
              const selected = card.id === selectedCardId;

              return (
                <Pressable
                  key={card.id}
                  accessibilityRole="button"
                  onPress={() => setSelectedCardId(card.id)}
                  style={[
                    styles.cardListItem,
                    selected && styles.cardListItemSelected,
                    { borderLeftColor: subject?.color_code ?? "#3182ce" },
                  ]}
                >
                  <Text style={styles.cardListTitle}>{card.title}</Text>
                  <Text style={styles.cardListSubject}>{subject?.name ?? "未分類"}</Text>
                </Pressable>
              );
            })}
          </View>
        </Card.Content>
      </Card>

      {cardForm && (
        <Card style={styles.panel}>
          <Card.Content>
            <Text variant="titleMedium" style={styles.sectionTitle}>
              カード編集
            </Text>

            <TextInput
              mode="outlined"
              label="カード名"
              value={cardForm.title}
              onChangeText={(value) => updateCardForm({ title: value })}
              style={styles.input}
            />

            <Text style={styles.fieldLabel}>教科</Text>
            <View style={styles.subjectChips}>
              {subjects.map((subject) => (
                <Pressable
                  key={subject.id}
                  accessibilityRole="button"
                  onPress={() => updateCardForm({ subject_id: subject.id })}
                  style={[
                    styles.subjectChip,
                    { borderColor: subject.color_code },
                    cardForm.subject_id === subject.id && {
                      backgroundColor: `${subject.color_code}20`,
                    },
                  ]}
                >
                  <Text style={[styles.subjectChipText, { color: subject.color_code }]}>
                    {subject.name}
                  </Text>
                </Pressable>
              ))}
            </View>

            <TextInput
              mode="outlined"
              label="レンズ名"
              value={cardForm.lens_name}
              onChangeText={(value) => updateCardForm({ lens_name: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="ツール名"
              value={cardForm.tool_name}
              onChangeText={(value) => updateCardForm({ tool_name: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="定義"
              multiline
              value={cardForm.definition}
              onChangeText={(value) => updateCardForm({ definition: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="説明"
              multiline
              value={cardForm.explanation}
              onChangeText={(value) => updateCardForm({ explanation: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="効果"
              multiline
              value={cardForm.effect}
              onChangeText={(value) => updateCardForm({ effect: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="具体例"
              multiline
              value={cardForm.example}
              onChangeText={(value) => updateCardForm({ example: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="活用例"
              multiline
              value={cardForm.application_example}
              onChangeText={(value) =>
                updateCardForm({ application_example: value })
              }
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="イラストURL"
              value={cardForm.illustration_url}
              onChangeText={(value) => updateCardForm({ illustration_url: value })}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="思考タグ（カンマ区切り）"
              value={thinkingTags}
              onChangeText={setThinkingTags}
              style={styles.input}
            />
            <TextInput
              mode="outlined"
              label="キャリアタグ（カンマ区切り）"
              value={careerTags}
              onChangeText={setCareerTags}
              style={styles.input}
            />

            <Divider style={styles.divider} />
            <View style={styles.saveRow}>
              <Button
                mode="contained"
                icon={() => <Save size={16} color="#fff" />}
                loading={isSaving}
                disabled={isSaving}
                onPress={handleSaveCard}
                buttonColor={selectedSubject?.color_code ?? "#3182ce"}
              >
                保存
              </Button>
              {cardForm.id && (
                <Button
                  mode="outlined"
                  icon={() => <Archive size={16} color="#e53e3e" />}
                  textColor="#e53e3e"
                  onPress={handleArchiveCard}
                >
                  非公開
                </Button>
              )}
            </View>
          </Card.Content>
        </Card>
      )}

      <View style={{ height: 32 }} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f6f8fb",
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#f6f8fb",
  },
  centeredTitle: {
    fontWeight: "700",
    marginBottom: 8,
  },
  centeredText: {
    color: "#667085",
    lineHeight: 22,
    textAlign: "center",
    marginBottom: 18,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingTop: 18,
    paddingBottom: 12,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  backButton: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontWeight: "700",
  },
  subtitle: {
    color: "#667085",
    marginTop: 4,
    lineHeight: 20,
  },
  panel: {
    marginHorizontal: 14,
    marginTop: 14,
    borderRadius: 8,
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  sectionTitle: {
    fontWeight: "700",
    marginBottom: 12,
  },
  subjectForm: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subjectNameInput: {
    flex: 1,
    backgroundColor: "#fff",
  },
  colorInput: {
    width: 110,
    backgroundColor: "#fff",
  },
  cardList: {
    gap: 8,
  },
  cardListItem: {
    borderLeftWidth: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "#fff",
  },
  cardListItemSelected: {
    backgroundColor: "#edf6ff",
    borderColor: "#3182ce",
  },
  cardListTitle: {
    color: "#1f2937",
    fontWeight: "700",
  },
  cardListSubject: {
    color: "#667085",
    fontSize: 12,
    marginTop: 2,
  },
  input: {
    marginBottom: 12,
    backgroundColor: "#fff",
  },
  fieldLabel: {
    color: "#4b5563",
    fontWeight: "700",
    marginBottom: 8,
  },
  subjectChips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  subjectChip: {
    borderWidth: 1,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: "#fff",
  },
  subjectChipText: {
    fontWeight: "700",
    fontSize: 12,
  },
  divider: {
    marginVertical: 12,
  },
  saveRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
});
